import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth } from './AuthContext';
import { useOrgPermissions } from '../hooks/useOrgPermissions';
import { getOrganizationCapabilities } from '../services/commerce.service';
import { isOrgManager, isOrgOwner } from '../utils/orgAccess';
import { ROUTES } from '../constants/routes';

export const CAPABILITIES_REFRESH_EVENT = 'safemeet:capabilities-refresh';

const OrgCapabilitiesContext = createContext(null);

const COSTLY = new Set([
  'meetings.create',
  'meetings.start',
  'meetings.finalize',
  'meetings.record',
  'org.members.add',
  'notifications.sms',
  'ai.run',
  'storage.upload',
]);

function emptySnapshot() {
  return {
    access_state: 'active',
    capabilities: {},
    banner: { show: false },
    current_plan: { has_plan: false },
    limits: {},
  };
}

export function OrgCapabilitiesProvider({ children }) {
  const { user, activeOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState(emptySnapshot);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!activeOrganization?.id) {
      setSnapshot(emptySnapshot());
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getOrganizationCapabilities(activeOrganization.id);
      setSnapshot(data && typeof data === 'object' ? data : emptySnapshot());
      setError(null);
    } catch (e) {
      setError(e);
      setSnapshot(emptySnapshot());
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const handler = () => {
      refresh();
    };
    window.addEventListener(CAPABILITIES_REFRESH_EVENT, handler);
    return () => window.removeEventListener(CAPABILITIES_REFRESH_EVENT, handler);
  }, [refresh]);

  const canManageBilling = isOrgOwner(user?.organizations, activeOrganization?.id)
    || isOrgManager(user?.organizations, activeOrganization?.id);

  const value = useMemo(
    () => ({
      snapshot,
      loading,
      error,
      refresh,
      accessState: snapshot.access_state || 'active',
      isReadOnly: snapshot.access_state === 'read_only',
      isExpiring: snapshot.access_state === 'expiring',
      banner: snapshot.banner || { show: false },
      canManageBilling,
      catalogPath: snapshot.banner?.action_path || `${ROUTES.BILLING}?section=purchase`,
    }),
    [snapshot, loading, error, refresh, canManageBilling],
  );

  return (
    <OrgCapabilitiesContext.Provider value={value}>
      {children}
    </OrgCapabilitiesContext.Provider>
  );
}

export function useOrgCapabilities() {
  const ctx = useContext(OrgCapabilitiesContext);
  const { can } = useOrgPermissions();
  const fallback = {
    snapshot: emptySnapshot(),
    loading: false,
    error: null,
    refresh: () => {},
    accessState: 'active',
    isReadOnly: false,
    isExpiring: false,
    banner: { show: false },
    canManageBilling: false,
    catalogPath: `${ROUTES.BILLING}?section=purchase`,
  };
  const state = ctx || fallback;

  const decision = useCallback(
    (capability) => state.snapshot?.capabilities?.[capability] || { allowed: true },
    [state.snapshot],
  );

  const canPackage = useCallback(
    (capability) => {
      if (state.loading && COSTLY.has(capability)) return false;
      if (state.error && COSTLY.has(capability)) return false;
      return decision(capability).allowed !== false;
    },
    [state.loading, state.error, decision],
  );

  const canPerform = useCallback(
    (rbacPermission, capability) => can(rbacPermission) && canPackage(capability || rbacPermission),
    [can, canPackage],
  );

  const createGate = useMemo(() => {
    const createDecision = decision('meetings.create');
    const rbacOk = can('meetings.create');
    const allowed = rbacOk && canPackage('meetings.create');
    return {
      allowed,
      rbacOk,
      reason: !rbacOk
        ? 'مجوز ایجاد جلسه ندارید.'
        : (createDecision.message || (state.loading ? 'در حال بررسی پکیج سازمان…' : null)),
      actionPath: createDecision.action_path || state.catalogPath,
    };
  }, [can, canPackage, decision, state.catalogPath, state.loading]);

  return {
    ...state,
    decision,
    canPackage,
    canPerform,
    createGate,
  };
}

export function notifyCapabilitiesChanged() {
  window.dispatchEvent(new Event(CAPABILITIES_REFRESH_EVENT));
}
